import React, { useState, useEffect, useRef } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import moment from 'moment';
import API from "../../config/api";
import { ToastContainer, toast } from "react-toastify";
import avatar from "./../../assets/assets_theme/img/avatar/1.jpg";
import CustomModalBlockUser from "../Bar/CustomModalBlockUser";
import CustomModalApproveUser from "../Bar/CustomModalApproveUser";
import CustomModalResetUser from "../Bar/CustomModalResetUser";
import adminUploadData from "../Helper/adminUpload";
import PopupImage from "../Bar/PopupImage";

const UserEdit = () => {
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState({
    id: null,
    student_id: null,
    custom_id: null, // mapped to student_id for API
    first_name: '',
    last_name: '',
    dob: null,
    email: '',
    address: '',
    gender_id: 0,
    role_id: 100,
    branch_id: 0,
    class_id: 1,
    teacher_id: 4,
    generation: null,
    phone_number: '',
    phone_number2: '',
    profile_url: '',
    filename: '',
    file_url: '',
    bio: '',
    test_available: 2,
    document1: '',
    document2: '',
    document3: '',
    national_card_url_filename: '',
    full_national_card_url: ''
  });

  const [allBranch, setAllBranch] = useState([]);
  const [dob, setDob] = useState(null);
  const [success, setSuccess] = useState(null);
  const [errors, setErrors] = useState({});

  // Constants
  const limit = 100;
  const page = 1;

  // Refs
  const inputRefs = {
    first_name: useRef(null),
    last_name: useRef(null),
    phone_number: useRef(null),
    student_id: useRef(null),
    dob: useRef(null),
    address: useRef(null),
    branch: useRef(null),
    batch: useRef(null),
    profile_url: useRef(null),
    national_card: useRef(null),
  };

  useEffect(() => {
    getAllBranch();

    const id = searchParams.get("id");
    const successParam = searchParams.get("success");

    if (id) {
      getOneUser(id);
    } // Removed the setTimeout setState hack because functional components re-render on mount anyway? No, but initial render is fine.

    if (successParam) {
      toast.success("Your action is successful", {
        data: {
          title: "Success toast",
          text: "This is a success message",
        },
      })
    }
  }, [searchParams]);

  const getAllBranch = async () => {
    try {
      let query = `page=${page}&limit=${limit}`
      let results = await API.getAllBranch(query);
      let data = results.data;
      setAllBranch(data.school_branch);
    } catch (error) {
      console.log(error)
    }
  }

  const getOneUser = async (id) => {
    try {
      const query = `user_id=${id || users.id}`;
      const results = await API.getOneUser(query);
      const data = results.data.user;

      const updatedUser = {
        ...users,
        ...data,
        student_id: data.custom_id,
        full_national_card_url: data.id_card_url,
        id: id || users.id // ensure id is set
      };

      setUsers(updatedUser);

      if (data.dob) {
        convertDateToString(data.dob);
      }

      // We don't need addRefValue anymore since we validate against 'users' state directly, 
      // providing we map keys correctly.

    } catch (error) {
      console.log(error);
    }
  };

  const onProfileChange = async (event) => {
    event.preventDefault();
    let file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      let results = await API.adminUpload(formData, (percentage) => { });

      if (results) {
        setUsers(prev => ({
          ...prev,
          profile_url: results.full_url,
          file_url: results.filename
        }));
        setErrors(prev => ({ ...prev, profile_url: "" }));
      }

    } catch (error) {
      console.log(error)
    }
  }

  const convertDate = (dateString) => {
    return moment(dateString, 'YYYYMMDD').format('YYYY-MM-DD');
  };

  const convertDateToString = (date) => {
    setDob(moment(date).format("YYYYMMDD"));
  };

  const validateFields = () => {
    const newErrors = {};
    let firstEmptyField = null;

    // Mapping of field names to user properties and validation rules
    const validationRules = [
      { field: 'first_name', value: users.first_name, type: 'string' },
      { field: 'last_name', value: users.last_name, type: 'string' },
      { field: 'dob', value: dob, type: 'string' }, // Use dob state variable
      { field: 'phone_number', value: users.phone_number, type: 'number_string' }, // It's string but contains numbers
      // { field: 'address', value: users.address, type: 'string' },
      { field: 'student_id', value: users.student_id, type: 'number_string' },
      // { field: 'branch', value: users.branch_id, type: 'select' },
      // { field: 'batch', value: users.generation, type: 'select' },
      // { field: 'profile_url', value: users.profile_url, type: 'url' },
      // { field: 'national_card', value: users.full_national_card_url, type: 'url' },
    ];

    validationRules.forEach(({ field, value, type }) => {
      let isValid = true;
      if (type === 'string') {
        if (!value || (typeof value === 'string' && !value.trim())) isValid = false;
      } else if (type === 'number_string') {
        if (!value || (typeof value === 'string' && !value.trim())) isValid = false;
      } else if (type === 'select') {
        if (!value || value === 0 || value === "0") isValid = false;
      } else if (type === 'url') {
        if (!value || (typeof value === 'string' && !value.trim())) isValid = false;
      }

      if (!isValid) {
        newErrors[field] = `${field.replace('_', ' ').charAt(0).toUpperCase() + field.replace('_', ' ').slice(1)} is required`;
        if (!firstEmptyField) firstEmptyField = field;
      }
    });

    // Explicit profile_url check
    // if (!users.profile_url || users.profile_url.trim() === "") {
    //   newErrors.profile_url = "Profile photo is required";
    //   if (!firstEmptyField) firstEmptyField = "profile_url";
    // }

    // Explicit national_card check
    // if (!users.full_national_card_url || users.full_national_card_url === "") {
    //   newErrors.national_card = "National Card is required";
    //   if (!firstEmptyField) firstEmptyField = "national_card";
    // }


    setErrors(newErrors);

    if (firstEmptyField && inputRefs[firstEmptyField].current) {
      inputRefs[firstEmptyField].current.focus();
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (name, value) => {
    // Map generic 'branch' / 'batch' names to user props if needed for error clearing?
    // Actually we just set error[name] = ""
    setErrors(prev => ({ ...prev, [name]: "" }));
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateFields()) return;

    let data = {
      id: users.id,
      first_name: users.first_name,
      last_name: users.last_name,
      profile_url: users.profile_url, // Original uses this.state.users.profile_url? No, look at line 234: profile_url: this.state.users.profile_url.
      // Wait, original line 193 in UserEdit.js: this.state.users.file_url= results.filename
      // Line 234: profile_url: this.state.users.profile_url.
      // Line 213 check: if (data.profile_url.includes("http")) delete data.profile_url
      // This refers to `UserView` logic?
      // In `UserEdit.js` original code:
      // Line 234: profile_url: this.state.users.profile_url.
      // Line 249: id_card_url : this.state.users.full_national_card_url

      email: users.email,
      address: users.address,
      gender_id: users.gender_id,
      role_id: users.role_id,
      phone_number: users.phone_number,
      phone_number2: users.phone_number2,
      class_id: users.class_id,
      teacher_id: users.teacher_id,
      dob: convertDate(dob),
      branch_id: users.branch_id,
      generation: users.generation,
      custom_id: users.student_id, // Important mapping
      bio: users.bio,
      test_available: users.test_available,
      id_card_url: users.full_national_card_url,
    };

    try {
      let results = await API.postUser(data);
      if (results.result) {
        toast.success("Your action is successful", {
          data: {
            title: "Success toast",
            text: "This is a success message",
          },
        })
        setSuccess(results.result);
      }

    } catch (error) {
      console.log(error)
    }
  }

  const blockUser = async (event) => {
    event.preventDefault();
    let data = {
      user_id: users.id,
      block: users.block ? 0 : 1,
    }
    try {
      let results = await API.postBlockUser(data);
      if (results.result) {
        toast.success("Your action is successful", {
          data: {
            title: "Success toast",
            text: "This is a success message",
          },
        })
        getOneUser(users.id);
      }
    } catch (error) {
      console.log(error)
    }
  }

  const approveUser = async (event) => {
    event.preventDefault();
    let data = {
      id: users.id,
      approve: users.approve ? 0 : 1
    }
    try {
      let results = await API.approveUser(data);
      if (results.result) {
        toast.success("Your action is successful", {
          data: {
            title: "Success toast",
            text: "This is a success message",
          },
        })
        getOneUser(users.id);
      }
    } catch (error) {
      console.log(error)
    }
  }

  const resetUser = async (event) => {
    event.preventDefault();
    let data = {
      user_id: users.id
    }
    try {
      let results = await API.resetUser(data);
      if (results.result) {
        toast.success("Your action is successful", {
          data: {
            title: "Success toast",
            text: "This is a success message",
          },
        })
        getOneUser(users.id);
      }
    } catch (error) {
      console.log(error)
    }
  }

  const uploadNationalCardId = async (e) => {
    const upload = await adminUploadData(e);
    if (upload) {
      setUsers(prev => ({
        ...prev,
        full_national_card_url: upload.full_url,
        national_card_url_filename: upload.filename
      }));
      setErrors(prev => ({ ...prev, national_card: "" }));
    }
  };

  const generationOptions = [];
  for (let i = 0; i < 50; i++) {
    generationOptions.push(<option value={i} key={i}> {i + 1}</option>);
  }

  return (
    <div className="col-12">
      <ToastContainer />
      {success && (
        <Navigate to="/userindex?success=true" replace={true} />
      )}
      <form className="card" onSubmit={handleSubmit}>
        <h4 className="card-title fw-400">User profile</h4>
        <br />

        <div className="card-body">
          <div className="flexbox gap-items-4">
            <img className="avatar avatar-xl" src={users.profile_url ? users.profile_url : avatar} alt="..."
              ref={inputRefs.profile_url}
              // name= "profile_url" // Removed name as img doesn't strictly support it for input purposes usually?
              style={{
                border: errors.profile_url ? "1px solid red" : "1px solid transparent",
                borderRadius: "50%",
              }}
            />

            <div className="flex-grow">
              <h5>{users.first_name + ' ' + users.last_name}</h5>
              <div className="d-flex flex-column flex-sm-row gap-items-2 gap-y mt-16">
                <div className="file-group file-group-inline">
                  <button className="btn btn-sm btn-w-lg btn-outline btn-round btn-secondary file-browser" type="button">Change Picture</button>
                  <input type="file" onChange={onProfileChange} />
                </div>
              </div>
              {errors.profile_url && <span style={{ color: "red" }}>{errors.profile_url}</span>}
            </div>
          </div>

          <hr />

          <div className="row">
            <div className="form-group col-md-6">
              <label className="require" htmlFor="first_name">First Name</label>
              <input type="text" className="form-control" id="first_name" name="first_name"
                value={users.first_name}
                ref={inputRefs.first_name}
                style={{
                  border: errors.first_name ? "1px solid red" : "",
                }}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, first_name: val }));
                  handleInputChange('first_name', val);
                }}
              />
              {errors.first_name && <span style={{ color: "red" }}>{errors.first_name}</span>}
            </div>

            <div className="form-group col-md-6">
              <label >Branch</label>
              <select className="form-control" title="&#xA0;"
                value={users.branch_id}
                name="branch"
                ref={inputRefs.branch}
                style={{
                  border: errors.branch ? "1px solid red" : "",
                }}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, branch_id: val }));
                  handleInputChange('branch', val);
                }}
              >
                <option value={0}> Please select branch </option>
                {
                  allBranch.map((branch) => {
                    return <option value={branch.id} key={branch.id}> {branch.name}</option>
                  })
                }
              </select>
              {errors.branch && (
                <span style={{ color: "red" }}>{errors.branch}</span>
              )}
            </div>
            <div className="form-group col-md-6">
              <label className="require" htmlFor="last_name">Last Name</label>
              <input type="text" className="form-control" id="last_name" name="last_name"
                value={users.last_name}
                ref={inputRefs.last_name}
                style={{
                  border: errors.last_name ? "1px solid red" : "",
                }}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, last_name: val }));
                  handleInputChange('last_name', val);
                }}
              />
              {errors.last_name && <span style={{ color: "red" }}>{errors.last_name}</span>}

            </div>
            <div className="form-group col-md-6">
              <label className="require" htmlFor="dob">Date of birth</label>
              <input type="text" name="dob"
                className="form-control" id="dob"
                value={dob || ''}
                ref={inputRefs.dob}
                style={{
                  border: errors.dob ? "1px solid red" : "",
                }}
                onChange={(event) => {
                  setDob(event.target.value);
                  handleInputChange('dob', event.target.value);
                }}
              />
              {errors.dob && <span style={{ color: "red" }}>{errors.dob}</span>}

            </div>

            <div className="form-group col-md-6">
              <label className="">Student ID</label>
              <input
                className="form-control"
                type="text"
                name="student_id"
                value={users.student_id}
                ref={inputRefs.student_id}
                style={{
                  border: errors.student_id ? "1px solid red" : "",
                }}
                onChange={(event) => {
                  const value = event.target.value;
                  if (!/^\d*$/.test(value)) return;

                  setUsers(prev => ({ ...prev, student_id: value }));
                  handleInputChange('student_id', value);
                }}
              />

              {errors.student_id && <span style={{ color: "red" }}>{errors.student_id}</span>}
            </div>

            <div className="form-group col-md-6">
              <label className="r">Gender</label>
              <select className="form-control" title="&#xA0;" value={users.gender_id}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, gender_id: val }));
                }}
              >
                <option value={1}> Male</option>
                <option value={2}>Female</option>
                <option value={0}>Other</option>
              </select>
            </div>


            <div className="form-group col-md-6">
              <label className="">Phone Number</label>
              <input
                className="form-control"
                type="text"
                name="phone_number"
                value={users.phone_number}
                ref={inputRefs.phone_number}
                style={{
                  border: errors.phone_number ? "1px solid red" : "",
                }}
                onChange={(event) => {
                  const value = event.target.value;
                  if (!/^\d*$/.test(value)) return;

                  setUsers(prev => ({ ...prev, phone_number: value }));
                  handleInputChange('phone_number', value);
                }}
              />

              {errors.phone_number && <span style={{ color: "red" }}>{errors.phone_number}</span>}
            </div>
            <div className="form-group col-md-6">
              <label className="">Role</label>
              <select className="form-control" title="&#xA0;" value={users.role_id}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, role_id: val }));
                }}
              >
                <option value={1}> Admin</option>
                <option value={10}>Teacher</option>
                <option value={100}>Student</option>
              </select>
            </div>

            <div className="form-group col-md-6">
              <label className="">Batch</label>
              <select className="form-control" title="&#xA0;" name="batch"
                value={users.generation || ''}
                ref={inputRefs.batch}
                style={{
                  border: errors.batch ? "1px solid red" : "",
                }}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, generation: val }));
                  handleInputChange('batch', val);
                }}
              >
                <option value={0}> Please select batch </option>
                {generationOptions}
              </select>
              {errors.batch && <span style={{ color: "red" }}>{errors.batch}</span>}
            </div>
            <div className="form-group col-md-6">
              <label >Email</label>
              <input className="form-control" type="email" name="email"
                value={users.email}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, email: val }));
                }}
              />
            </div>

            <div className="form-group col-md-6">
              <label className="">Address</label>
              <input className="form-control" type="text" name="address"
                value={users.address}
                ref={inputRefs.address}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, address: val }));
                  handleInputChange('address', val);
                }}
              />
              {/* Note: Original file had cut off here, I assume address input continues similar to others */}
            </div>

            <div className="form-group col-md-6">
              <label className="">Bio</label>
              <input className="form-control" type="text"
                value={users.bio}
                onChange={(event) => {
                  const val = event.target.value;
                  setUsers(prev => ({ ...prev, bio: val }));
                }}
              />
            </div>
            <div className="form-group col-md-6">
              <label className="">National card ID</label>
              <input type="file" className="form-control" accept="image/*"
                ref={inputRefs.national_card}
                name="national_card"
                style={{
                  border: errors.national_card ? "1px solid red" : "",
                }}
                onChange={(e) => uploadNationalCardId(e)} />
              {errors.national_card && <span style={{ color: "red" }}>{errors.national_card}</span>}
              <div style={{
                marginTop: '10px',
                textAlign: 'center',
                // MaxHeight: '50px',
                height: users.full_national_card_url ? '250px' : 0,
                // objectFit: 'cover'
                display: 'flex', // Flexbox for alignment
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                {
                  users.full_national_card_url && <PopupImage
                    thumbnailUrl={users.full_national_card_url}
                    imageUrl={users.full_national_card_url}
                  />
                }
              </div>

            </div>
          </div>

          <div className="row">
          </div>
          <span>&nbsp;</span>
          <button type="button"
            title="Block"
            className={`btn ${users.block ? 'btn-danger' : 'btn-warning'}`}
            data-toggle="modal"
            data-target="#modal-center-blockuser"
          >
            {users.block ? 'Unblock' : 'Block'} <i className="fa fa-minus-square" ></i>
          </button>
          <span>&nbsp;</span>
          <button type="button"
            title="Approve"
            className={`btn ${users.approve ? 'btn-primary' : 'btn-info'}`}
            data-toggle="modal"
            data-target="#modal-center-approveuser"
          >
            {users.approve ? 'Approve' : 'Disapprove'} <i className="fa fa-minus-square"></i>
          </button>
          <span>&nbsp;</span>
          <button type="button"
            title="Reset"
            className="btn btn-danger"
            data-toggle="modal"
            data-target="#modal-center-resetuser"
          >
            Reset <i className="fa fa-refresh"></i>
          </button>

        </div>

        <footer className="card-footer text-right">
          <Link className="btn btn-secondary" to="/userindex" >Cancel</Link>
          <button className="btn btn-primary" type="submit">Submit</button>
        </footer>

      </form>
      {
        users && (
          <>
            <CustomModalBlockUser
              data={users}
              title={users.active ? "Do you really want to unblock this user?" : "Do you really want to block this user?"}
              blockUser={blockUser}
              approveUser={approveUser}
            />
            <CustomModalApproveUser
              data={users}
              title={users.approve ? "Do you really want to approve this user?" : "Do you really want to disapprove this user?"}
              approveUser={approveUser}
            />
            <CustomModalResetUser
              data={users}
              title="Do you really want to reset this user?"
              approveUser={resetUser}
            />
          </>
        )
      }
    </div>
  );
}

export default UserEdit;
